import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WORKS } from "@/data/works";
import useAccentSampler from "@/hooks/useAccentSampler";
import { useT } from "@/i18n/LanguageContext";
import ResponsiveArt from "@/components/ResponsiveArt";

// Curated lead works for the hero carousel — six recent, visually distinct pieces
// chosen so they don't duplicate the first plates of the Showcase scroll below.
const LEAD_SLUGS = [
  "eye-of-enlightenment",
  "she-was-the-mirror",
  "when-the-light-stayed",
  "when-the-ocean-listens",
  "roots-of-rebirth",
  "untitled-2026-04",
];

const HERO_IMAGES = LEAD_SLUGS
  .map((slug) => WORKS.find((w) => w.slug === slug))
  .filter(Boolean)
  .map((w, i) => ({
    slug: w.slug,
    src: w.image,
    alt: w.alt || w.title,
    caption: `№ ${String(i + 1).padStart(2, "0")} — ${w.title} (${w.year})`,
  }));

const CYCLE_MS = 5500;

export default function Hero() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 1, 0]);

  // Subtle damped mouse-following parallax for the logotype.
  // The whole title drifts ~14px toward the cursor with a heavy spring.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 60, damping: 18, mass: 0.6 });

  // Auto-cycling hero image (cross-fades through the archive).
  // Start from a random position so every refresh opens on a different painting.
  const [idx, setIdx] = useState(() =>
    HERO_IMAGES.length > 0 ? Math.floor(Math.random() * HERO_IMAGES.length) : 0
  );
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Range: -14px ... +14px depending on cursor position
      mx.set(((e.clientX - w / 2) / w) * 14);
      my.set(((e.clientY - h / 2) / h) * 14);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || HERO_IMAGES.length <= 1 || hovering) return;

    const t = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, CYCLE_MS);
    return () => clearInterval(t);
  }, [hovering]);

  // Preload the next image so the cross-fade is seamless.
  useEffect(() => {
    if (HERO_IMAGES.length <= 1) return;
    const nextSrc = HERO_IMAGES[(idx + 1) % HERO_IMAGES.length].src;
    const img = new Image();
    img.src = nextSrc;
  }, [idx]);

  const current = HERO_IMAGES[idx] || HERO_IMAGES[0];

  // Sample the dominant tone of the current painting and expose it as
  // `--accent-dynamic`. The scroll-progress bar + Hero CTA underline pick it up.
  useAccentSampler(current?.src);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative w-full h-screen overflow-hidden bg-bone"
    >
      {/* Massive bleeding image — full-bleed backdrop on every breakpoint.
          The wordmark and manifesto sit ON TOP of it. */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 w-full h-full overflow-hidden"
        onPointerEnter={() => setHovering(true)}
        onPointerLeave={() => setHovering(false)}
        data-hovering={hovering ? "true" : "false"}
      >
        <Link
          to={`/works/${current.slug}`}
          data-testid="hero-image-link"
          data-cursor="view"
          className="block absolute inset-0 group"
          aria-label={`Open ${current.caption}`}
          onPointerEnter={() => setHovering(true)}
        >
          <AnimatePresence initial={false}>
            <ResponsiveArt
              key={current.src}
              src={current.src}
              alt={current.alt}
              sizes="100vw"
              eager
              fadeIn={false}
              className="art-img absolute inset-0 w-full h-full object-cover"
              onImg={(attrs) => (
                <motion.img
                  {...attrs}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1 }}
                  transition={{ opacity: { duration: 1.4, ease: [0.16, 1, 0.3, 1] }, scale: { duration: 6, ease: [0.16, 1, 0.3, 1] } }}
                />
              )}
            />
          </AnimatePresence>
          {/* subtle "Look closer" affordance — appears only on hover */}
          <div className="absolute inset-0 flex items-end justify-end p-6 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-tl from-ink/40 via-ink/0 to-transparent">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-bone mb-10">
              {t("hero.look_closer")}
            </span>
          </div>
        </Link>
        <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6 z-10 pointer-events-none font-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-bone md:text-ink/70">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={current.caption}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block"
            >
              {current.caption}
            </motion.span>
          </AnimatePresence>
        </div>
        {/* tiny progress ticks — one per lead work, current = crimson */}
        <div className="absolute top-4 md:top-6 right-4 md:right-6 z-10 flex gap-2 pointer-events-none">
          {HERO_IMAGES.map((img, i) => (
            <span
              key={img.slug}
              className={`block h-px w-4 md:w-6 transition-colors duration-500 ${i === idx ? "bg-crimson" : "bg-bone/40 md:bg-ink/30"}`}
            />
          ))}
        </div>
      </motion.div>

      {/* Soft bone wash over the painting — keeps body type legible without
          smothering the painting. Cheap (no backdrop-filter) so it stays fast. */}
      <div
        aria-hidden
        className="absolute inset-0 z-[5] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(242,242,235,0.55) 0%, rgba(242,242,235,0.25) 35%, rgba(242,242,235,0.7) 100%)",
        }}
      />

      {/* Huge serif title — per-letter reveal + damped mouse parallax.
          Overlays the painting on every breakpoint. */}
      <motion.div
        style={{ y: titleY, opacity, x: sx, translateY: sy }}
        className="absolute top-0 left-0 right-0 z-20 pt-24 md:pt-[18vh] px-6 md:px-10 md:pr-10 pointer-events-none"
        data-testid="hero-logotype"
      >
        <Line text="Rushit Shah" baseDelay={0.2} withDot />
      </motion.div>

      {/* Manifesto + CTA — overlaid at the bottom on every breakpoint */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-16 md:bottom-16 left-0 right-0 px-6 md:px-10 z-30 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 md:gap-8"
      >
        <div className="max-w-md">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase mb-3">{t("hero.eyebrow_manifesto")}</p>
          <p className="font-serif text-base md:text-2xl leading-[1.32] md:leading-snug">
            {(() => {
              const tpl = t("hero.manifesto");
              const emph = t("hero.manifesto_emph");
              const [pre, post] = tpl.split("{argue}");
              return (
                <>
                  {pre}
                  <em className="text-crimson not-italic whitespace-nowrap">{emph}</em>
                  {post}
                </>
              );
            })()}
          </p>
        </div>

        <button
          data-testid="hero-cta"
          data-magnetic
          onClick={() => {
            const el = document.getElementById("works");
            if (el && window.__lenis) window.__lenis.scrollTo(el);
            else if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
          data-cursor="hover"
          className="group flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em]"
        >
          <span className="link-underline">{t("hero.cta")}</span>
          <span className="w-10 h-px bg-ink group-hover:bg-crimson transition-colors" />
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          >
            ↓
          </motion.span>
        </button>
      </motion.div>

      {/* Ticker bottom */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-ink/20 py-3 overflow-hidden bg-bone/70 backdrop-blur-sm">
        <div className="flex whitespace-nowrap ticker-roll motion-always marquee-track font-mono text-[10px] tracking-[0.3em] uppercase">
          {["a", "b"].map((id) => (
            <div key={`marquee-${id}`} className="flex items-center">
              <span>{t("hero.ticker_1")}</span><span className="mx-8 text-crimson">●</span>
              <span>{t("hero.ticker_2")}</span><span className="mx-8 text-crimson">●</span>
              <span>{t("hero.ticker_3")}</span><span className="mx-8 text-crimson">●</span>
              <span>{t("hero.ticker_4")}</span><span className="mx-8 text-crimson">●</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes ticker-roll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-roll { animation: ticker-roll 28s linear infinite; will-change: transform; }
        /* Ticker is exempt from reduced-motion — it's editorial signage, not motion garnish */
        @media (prefers-reduced-motion: reduce) {
          .ticker-roll.motion-always {
            animation: ticker-roll 36s linear infinite !important;
            transition: none !important;
          }
        }
      `}</style>
    </section>
  );
}

function Line({ text, baseDelay = 0, withDot = false }) {
  const letters = text.split("");
  return (
    <div className="overflow-y-hidden py-[0.18em]">
      <h1
        style={{ color: "#0A0A0A" }}
        className="font-burnts text-[16vw] sm:text-[18vw] md:text-[22vw] leading-[1.1] tracking-tight flex flex-nowrap whitespace-nowrap py-[0.12em]"
      >
        {letters.map((ch, i) => (
          <motion.span
            key={`${ch}-${i}`}
            initial={{ y: "110%", rotate: 6, opacity: 0 }}
            animate={{ y: "0%", rotate: 0, opacity: 1 }}
            transition={{
              duration: 1.1,
              ease: [0.16, 1, 0.3, 1],
              delay: baseDelay + i * 0.06,
            }}
            whileHover={ch === " " ? undefined : { y: -10, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }}
            className={`inline-block origin-bottom cursor-default ${ch === " " ? "pointer-events-none" : "pointer-events-auto"}`}
            style={ch === " " ? { width: "0.35em" } : undefined}
          >
            {ch === " " ? "\u00A0" : ch}
          </motion.span>
        ))}
        {withDot && (
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: baseDelay + letters.length * 0.06 + 0.05 }}
            whileHover={{ scale: 1.25, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
            style={{ color: "#FF2D2D" }}
            className="inline-block origin-bottom pointer-events-auto"
          >
            <motion.span
              animate={{ scale: [1, 1.08, 1], opacity: [1, 0.82, 1] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block"
            >
              .
            </motion.span>
          </motion.span>
        )}
      </h1>
    </div>
  );
}
